import { AuiSupernav } from '@avalara/skylab-sdk/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { isOneWebDomainRuntime } from '../../helpers';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { useFullBleedMain } from '../../hooks/use-full-bleed-main';
import { checkIsAuthenticated, getAccessToken } from '../../lib/helpers';
import { authRedirect } from '../../lib/side-effects';
import { fetchAccountData } from '../../lib/side-effects/accounts';
import { fetchNextBestProducts } from '../../lib/side-effects/next-best-product';
import { selectState } from '../../selectors';

type CatalogSurface = 'catalog' | 'pdp';

// Shared between containers/product-catalog/index.tsx and containers/product-catalog/product-detail.tsx —
// both fetch the same account/next-best-product data and render the same AvaApp nav override.
// BRGUI-2988: enableCrossSell is the master kill-switch for both surfaces; shouldShowProductCatalog
// additionally gates only the catalog grid. A PDP's own pdpFeatureFlag is enforced server-side.
export const useProductCatalogShell = (surface: CatalogSurface) => {
  const dispatch = useAppDispatch();
  const {
    account: { accountId, cam, camOwned, customerAccountId, obligations, user },
    data: { isLoading },
    features,
    nextBestProduct,
  } = useSelector(selectState);

  const isSurfaceEnabled =
    Boolean(features.ENABLECROSSSELL) && (surface === 'pdp' || Boolean(features.SHOULDSHOWPRODUCTCATALOG));

  useEffect(() => {
    if (!isSurfaceEnabled) return;

    if (!checkIsAuthenticated()) {
      authRedirect();
      return;
    }

    dispatch(fetchAccountData());
  }, [isSurfaceEnabled]);

  useEffect(() => {
    if (!isSurfaceEnabled || !accountId) return;

    dispatch(fetchNextBestProducts());
  }, [isSurfaceEnabled, accountId]);

  useEffect(() => {
    if (!accountId || isLoading) return;
    if (!isOneWebDomainRuntime() || typeof window.AvaApp?.renderAuiNav !== 'function') return;

    window.AvaApp.renderAuiNav();

    return () => {
      if (typeof window.AvaApp?.removeAuiNav === 'function') {
        window.AvaApp.removeAuiNav();
      }
    };
  }, [accountId, isLoading]);

  const renderNavigation = () => {
    if (!accountId || isLoading) return null;

    return (
      <div className='product-catalog-nav'>
        <AuiSupernav accountId={accountId.toString()} requestCurrentAccessToken={getAccessToken} />
      </div>
    );
  };

  const shouldRender = isSurfaceEnabled && checkIsAuthenticated();

  // Both catalog surfaces paint the full-bleed nav strip, so they need the shell's <main> to contain
  // its 100vw overhang for as long as they're mounted.
  useFullBleedMain(shouldRender);

  return {
    dispatch,
    accountId,
    cam,
    camOwned,
    customerAccountId,
    obligations,
    user,
    isLoading,
    features,
    nextBestProduct,
    renderNavigation,
    shouldRender,
  };
};
